import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import { toast } from 'react-hot-toast';
import Loader from './Loader';
import { useTheme } from '../contexts/ThemeContext';
import { Plus, ArrowLeft, Info, CheckSquare, FileText, Video } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import DOMPurify from 'dompurify';

const SharedMeetingView = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();
  
  const [meeting, setMeeting] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [activeTab, setActiveTab] = useState('notes');

  useEffect(() => {
    let isMounted = true;

    const fetchMeeting = async () => {
      if (!isLoaded) return;
      
      try {
        const ts = Date.now();
        const url = `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/meetings/share/${shareToken}/view?ts=${ts}`;

        const response = await fetch(url, { cache: 'no-store' });
        
        if (!response.ok) {
          throw new Error('Failed to fetch meeting');
        }

        const data = await response.json();
        
        if (data.processed_transcript && typeof data.processed_transcript === 'string') {
          data.processed_transcript = JSON.parse(data.processed_transcript);
        }
        
        if (isMounted) {
          setMeeting(data);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error fetching meeting:', error);
          setError('Failed to load meeting');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchMeeting();

    return () => {
      isMounted = false;
    };
  }, [shareToken, isLoaded, isSignedIn]);

  const handleAddToAccount = async () => {
    setIsAdding(true);
    try {
      const response = await authenticatedFetch(
        `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/meetings/${shareToken}`,
        {
          method: 'POST',
          cache: 'no-store'
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success('Meeting added to your account');
        navigate(`/meetings/${data.id}`);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Failed to add meeting');
      }
    } catch (error) {
      console.error('Error adding meeting:', error);
      toast.error('Failed to add meeting');
    } finally {
      setIsAdding(false);
    }
  };

  // Markdown components to match full meeting view styling
  const markdownComponents = {
    h1: ({ node, ...props }) => (
      <h1 className="text-3xl font-bold my-4 text-theme" {...props} />
    ),
    h2: ({ node, ...props }) => (
      <h2 className="text-2xl font-bold my-4 text-theme" {...props} />
    ),
    h3: ({ node, ...props }) => (
      <h3 className="text-xl font-bold my-4 text-theme" {...props} />
    ),
    p: ({ node, ...props }) => (
      <p className="my-2 text-theme" {...props} />
    ),
    ul: ({ node, ...props }) => (
      <ul className="list-disc list-inside my-2 text-theme" {...props} />
    ),
    ol: ({ node, ...props }) => (
      <ol className="list-decimal list-inside my-2 text-theme" {...props} />
    ),
    li: ({ node, ...props }) => (
      <li className="my-1 text-theme" {...props} />
    ),
    strong: ({ node, ...props }) => (
      <strong className="font-bold text-theme" {...props} />
    ),
    blockquote: ({ node, ...props }) => (
      <blockquote className="border-l-4 border-gray-300 pl-4 my-2 italic text-theme" {...props} />
    ),
    code: ({ node, inline, ...props }) => (
      inline ? (
        <code className="bg-gray-100 rounded px-1 text-gray-800" {...props} />
      ) : (
        <code className="block bg-gray-100 p-2 rounded my-2 overflow-x-auto text-gray-800" {...props} />
      )
    )
  };

  if (!isLoaded) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        <p>{error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="p-4 text-gray-500">
        Meeting not found
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-theme theme-transition overflow-auto">
      <header className="bg-theme border-b border-theme">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          {/* Enhanced Navigation Breadcrumb */}
          <nav className="flex items-center text-sm mb-4">
            {isSignedIn ? (
              <button
                onClick={() => navigate('/meetings')}
                className="text-accent hover:text-theme transition-colors flex items-center gap-2 group"
              >
                <ArrowLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
                <span className="hover:underline">Back to Meetings</span>
              </button>
            ) : (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 text-accent">
                  <div className="w-2 h-2 rounded-full bg-primary"></div>
                  <span>Shared Meeting</span>
                </div>
                <div className="h-4 w-px bg-border"></div>
                <Link
                  to="/sign-up"
                  className="text-accent hover:text-theme transition-colors hover:underline font-medium"
                >
                  Create a free account to save this
                </Link>
              </div>
            )}
          </nav>

          {/* Enhanced Header Content */}
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-4 mb-3">
                <div className="p-3 rounded-xl bg-primary">
                  <FileText size={24} className="text-theme" />
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-bold text-theme mb-2 break-words">
                    {meeting.title}
                  </h1>
                  <div className="flex items-center gap-4 text-sm text-accent">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-accent"></div>
                      <span>
                        {new Date(meeting.date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    {meeting.processed_transcript?.content && (
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        <span>AI Processed</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Enhanced Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              {isSignedIn ? (
                <button
                  onClick={handleAddToAccount}
                  disabled={isAdding}
                  className="group px-6 py-3 rounded-xl flex items-center justify-center gap-3 bg-primary hover:bg-primary-hover text-theme transition-all duration-200 hover:scale-[1.02] shadow-sm hover:shadow-md font-medium"
                  style={{ 
                    opacity: isAdding ? 0.6 : 1,
                    cursor: isAdding ? 'not-allowed' : 'pointer'
                  }}
                >
                  <Plus size={20} className={isAdding ? 'animate-spin' : 'group-hover:scale-110 transition-transform'} />
                  <span className="whitespace-nowrap">
                    {isAdding ? 'Adding to Account...' : 'Add to My Account'}
                  </span>
                </button>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/sign-up"
                    className="group px-6 py-3 rounded-xl bg-primary hover:bg-primary-hover text-theme transition-all duration-200 hover:scale-[1.02] shadow-sm hover:shadow-md text-center font-medium"
                  >
                    <span className="group-hover:scale-105 inline-block transition-transform">
                      Create Account
                    </span>
                  </Link>
                  <Link
                    to="/sign-in"
                    className="px-6 py-3 rounded-xl border-2 border-theme text-theme hover:bg-secondary transition-all duration-200 hover:scale-[1.02] text-center font-medium"
                  >
                    Sign In
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Enhanced Tab Navigation */}
          <div className="flex items-center gap-2 mt-6 overflow-x-auto pb-2">
            {[
              { id: 'notes', label: 'Processed Notes', icon: <FileText size={20} />, description: 'AI-enhanced notes' },
              { id: 'transcript', label: 'Raw Transcript', icon: <Video size={20} />, description: 'Original recording' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`group flex items-center gap-3 px-6 py-3 rounded-xl transition-all duration-200 whitespace-nowrap border-2 ${
                  activeTab === tab.id 
                    ? 'bg-primary text-theme border-primary shadow-sm' 
                    : 'bg-secondary text-accent hover:bg-primary-hover border-transparent hover:border-primary/20'
                }`}
              >
                <div className={`transition-transform ${activeTab === tab.id ? 'scale-110' : 'group-hover:scale-105'}`}>
                  {tab.icon}
                </div>
                <div className="text-left">
                  <div className="font-medium">{tab.label}</div>
                  <div className="text-xs opacity-70">{tab.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </header>

      <main>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {activeTab === 'notes' ? (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main Content Area */}
              <div className="lg:col-span-2 order-2 lg:order-1">
                <div className="bg-theme border border-theme rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                  <div className="p-6 sm:p-8">
                    <div className="prose max-w-none text-theme">
                      {meeting.processed_transcript?.content ? (
                        <ReactMarkdown
                          remarkPlugins={[remarkMath]}
                          rehypePlugins={[rehypeKatex, rehypeRaw]}
                          components={markdownComponents}
                        >
                          {DOMPurify.sanitize(meeting.processed_transcript.content)}
                        </ReactMarkdown>
                      ) : (
                        <div className="text-center py-12">
                          <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                            <FileText size={24} className="text-accent" />
                          </div>
                          <h3 className="text-lg font-medium text-theme mb-2">No Processed Notes</h3>
                          <p className="text-accent max-w-md mx-auto">
                            This meeting hasn't been processed yet. Processed notes provide AI-enhanced summaries and insights.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6 order-1 lg:order-2">
                {/* Summary Card */}
                <div className="bg-theme border border-theme rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-primary rounded-xl">
                        <Info size={20} className="text-theme" />
                      </div>
                      <h3 className="text-lg font-semibold text-theme">Summary</h3>
                    </div>
                    {meeting.processed_transcript?.summary ? (
                      <p className="text-theme leading-relaxed">
                        {meeting.processed_transcript.summary}
                      </p>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-accent text-sm">No summary available</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Action Items Card */}
                <div className="bg-theme border border-theme rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-primary rounded-xl">
                        <CheckSquare size={20} className="text-theme" />
                      </div>
                      <h3 className="text-lg font-semibold text-theme">Action Items</h3>
                    </div>
                    {meeting.processed_transcript?.action_items?.length > 0 ? (
                      <ul className="space-y-4">
                        {meeting.processed_transcript.action_items.map((item, index) => (
                          <li key={index} className="p-4 bg-secondary rounded-xl border border-border">
                            <p className="font-medium text-theme mb-2">{item.item}</p>
                            <div className="space-y-1">
                              {item.assignee && (
                                <div className="flex items-center gap-2 text-sm text-accent">
                                  <div className="w-1 h-1 rounded-full bg-accent"></div>
                                  <span>Assignee: {item.assignee}</span>
                                </div>
                              )}
                              {item.deadline && (
                                <div className="flex items-center gap-2 text-sm text-accent">
                                  <div className="w-1 h-1 rounded-full bg-accent"></div>
                                  <span>Due: {item.deadline}</span>
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-accent text-sm">No action items found</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Audio Player Card */}
                {meeting.audio_url && (
                  <div className="bg-theme border border-theme rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-primary rounded-xl">
                          <Video size={20} className="text-theme" />
                        </div>
                        <h3 className="text-lg font-semibold text-theme">Meeting Audio</h3>
                      </div>
                      <div className="bg-secondary rounded-xl p-4">
                        <audio 
                          className="w-full" 
                          controls
                          src={meeting.audio_url}
                          controlsList="nodownload"
                        >
                          Your browser does not support the audio element.
                        </audio>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Transcript View */
            <div className="bg-theme border border-theme rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden">
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-primary rounded-xl">
                    <Video size={20} className="text-theme" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-theme">Raw Transcript</h3>
                    <p className="text-sm text-accent">Original meeting recording transcript</p>
                  </div>
                </div>
                {meeting.transcript ? (
                  <div className="bg-secondary rounded-xl p-6">
                    <div className="whitespace-pre-wrap text-theme leading-relaxed font-mono text-sm">
                      {meeting.transcript}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                      <Video size={24} className="text-accent" />
                    </div>
                    <h3 className="text-lg font-medium text-theme mb-2">No Transcript Available</h3>
                    <p className="text-accent">
                      This meeting doesn't have a transcript yet.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Enhanced Promotional Section for Non-Signed Users */}
          {!isSignedIn && (
            <div className="mt-8">
              <div className="relative overflow-hidden bg-gradient-to-br from-primary via-secondary to-primary/50 rounded-3xl border border-theme shadow-lg">
                <div className="relative p-6 sm:p-8">
                  {/* Background decoration */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary rounded-full opacity-20 transform translate-x-8 -translate-y-8"></div>
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary rounded-full opacity-10 transform -translate-x-4 translate-y-4"></div>
                  
                  <div className="relative">
                    <div className="flex items-start gap-6 mb-6">
                      <div className="hidden sm:flex p-4 bg-primary rounded-2xl shadow-sm">
                        <Plus size={32} className="text-theme" />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-2xl sm:text-3xl font-bold text-theme mb-3">
                          Unlock the Full Power of Classify
                        </h2>
                        <p className="text-lg text-accent leading-relaxed">
                          Save this meeting and discover AI-powered features that transform how you study and work.
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-primary rounded-xl">
                          <FileText size={20} className="text-theme" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-theme mb-1">Save & Organize</h4>
                          <p className="text-sm text-accent">Add meetings to your personal library</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-primary rounded-xl">
                          <CheckSquare size={20} className="text-theme" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-theme mb-1">AI Study Tools</h4>
                          <p className="text-sm text-accent">Generate flashcards, quizzes & mind maps</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-primary rounded-xl">
                          <Video size={20} className="text-theme" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-theme mb-1">Export & Share</h4>
                          <p className="text-sm text-accent">Download PDFs and share with others</p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link 
                        to="/sign-up" 
                        className="group flex-1 bg-theme hover:bg-primary-hover border-2 border-theme text-theme px-8 py-4 rounded-xl font-semibold text-center transition-all duration-200 hover:scale-[1.02] shadow-sm hover:shadow-md"
                      >
                        <span className="group-hover:scale-105 inline-block transition-transform">
                          Create Free Account
                        </span>
                      </Link>
                      <Link 
                        to="/sign-in"
                        className="px-8 py-4 rounded-xl border-2 border-theme text-theme hover:bg-secondary font-medium text-center transition-all duration-200 hover:scale-[1.02] whitespace-nowrap"
                      >
                        Already have an account? Sign in
                      </Link>
                    </div>

                    <div className="flex items-center justify-center gap-2 mt-6 text-sm text-accent">
                      <div className="w-1.5 h-1.5 rounded-full bg-accent"></div>
                      <span>Free forever • No credit card required • Get started in 30 seconds</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SharedMeetingView; 